/**
 * Signed URLs for student headshots.
 *
 * The bucket is private, so every `<img>` needs a freshly signed URL. Two things make that
 * cheap enough for a sixty-child roster on school wifi:
 *
 *  1. Signing is batched — one request for the whole board, never one per row.
 *  2. Issued URLs are cached and reused until they are nearly expired, so a reload or a
 *     second roster does not re-sign what is already in hand.
 *
 * (2) matters more than it looks. A signed URL carries its token in the query string, so a
 * *newly signed* URL for an unchanged photo is a different URL to the browser cache — the
 * bytes get downloaded again despite being byte-identical. Reusing the URL is what lets the
 * ordinary HTTP cache do its job.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { STUDENT_PHOTO_BUCKET } from '../utils/studentPhotoImage';

/** How long a signed URL stays valid. */
const TTL_SECONDS = 60 * 60;

/**
 * Margin before expiry at which a URL stops being handed out.
 *
 * A teacher can hold the roster open across a whole pickup window; a URL that expired
 * mid-session would blank out faces exactly when someone is at the door.
 */
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

const STALE_MS = TTL_SECONDS * 1000 - REFRESH_BUFFER_MS;

/**
 * Where issued URLs are kept between renders and reloads.
 *
 * `sessionStorage`, not `localStorage`: a signed URL is a bearer credential for a child's
 * photo, and per-tab storage that dies with the tab is a much shorter window at rest than
 * one that persists on a shared staff device indefinitely. The photos themselves are not
 * stored here — only the short-lived URLs, a few hundred bytes each.
 */
const CACHE_KEY = 'bhcs.student-photo-urls.v1';

interface CachedUrl {
  url: string;
  /** Epoch ms. */
  expiresAt: number;
}

function readCache(): Record<string, CachedUrl> {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    return parsed as Record<string, CachedUrl>;
  } catch {
    // Disabled storage, private mode, or a corrupt entry. Caching is an optimisation; the
    // roster must still work without it.
    return {};
  }
}

function writeCache(entries: Record<string, CachedUrl>): void {
  try {
    const now = Date.now();
    // Pruned on write so a long-lived tab cannot accumulate every student ever viewed.
    const live = Object.entries(entries).filter(([, entry]) => entry.expiresAt > now);
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(Object.fromEntries(live)));
  } catch {
    // Over quota or unavailable — not worth failing a roster over.
  }
}

export const studentPhotoKeys = {
  urls: (paths: readonly string[]) => ['student-photo-urls', ...paths] as const,
};

/**
 * Map of object path -> signed URL for the paths given.
 *
 * Paths that fail to sign — the object was deleted, or this user cannot read it — are simply
 * absent from the map, and the avatar falls back to initials. A missing face is a cosmetic
 * loss; a thrown error would take the whole roster down with it.
 */
export function useStudentPhotoUrls(paths: readonly (string | null | undefined)[]) {
  const unique = Array.from(new Set(paths.filter((p): p is string => Boolean(p)))).sort();

  return useQuery<Record<string, string>>({
    queryKey: studentPhotoKeys.urls(unique),
    queryFn: async () => {
      const cache = readCache();
      const now = Date.now();

      const resolved: Record<string, string> = {};
      const unsigned: string[] = [];
      for (const path of unique) {
        const hit = cache[path];
        // The buffer means a URL handed out now is good for at least another five minutes.
        if (hit && hit.expiresAt - now > REFRESH_BUFFER_MS) resolved[path] = hit.url;
        else unsigned.push(path);
      }

      // Everything already in hand: the common case on a reload, and it costs no network at
      // all. Also covers switching between two rosters that share students.
      if (unsigned.length === 0) return resolved;

      const { data, error } = await supabase.storage
        .from(STUDENT_PHOTO_BUCKET)
        .createSignedUrls(unsigned, TTL_SECONDS);
      if (error) throw error;

      const expiresAt = Date.now() + TTL_SECONDS * 1000;
      for (const row of data ?? []) {
        if (row.path && row.signedUrl && !row.error) {
          resolved[row.path] = row.signedUrl;
          cache[row.path] = { url: row.signedUrl, expiresAt };
        }
      }
      writeCache(cache);
      return resolved;
    },
    enabled: unique.length > 0,
    staleTime: STALE_MS,
    // Held as long as the URLs stay valid. The default five minutes would discard them
    // whenever a teacher stepped off the roster screen, and coming back would re-sign — new
    // tokens, new URLs, every face downloaded again for nothing.
    gcTime: STALE_MS,
    // A failed signing round is not worth retrying into: the roster is fully usable without
    // faces, and retries would just delay the rows that did sign.
    retry: false,
  });
}
